import { IStoredCheckoutBasket } from "@checkout/types";
import { TPersonaValues } from "@api-clients/cats";
import { BaseAPI } from "@utils/base-api";
import { isAxiosError } from "axios";

import { CheckoutStatus, type CheckoutBasket } from "@/primary-models";

import {
  CreateCheckoutBasketResponse,
  ExpireCheckoutSessionRequest,
  ExpireCheckoutSessionResponse,
  IBasketConfirmedRequest,
  IBasketConfirmedResponse,
  StartDirectSalesCheckoutRequest,
  StartDirectSalesCheckoutResult,
} from "./basket.types";
import {
  IGetPriceEstimateRequest,
  IGetPriceEstimateResponse,
} from "./pricing.types";
import {
  CHECKOUT_PRICE_ESTIMATE_PATH,
  CHECKOUT_PAYMENTS_BASKET_CONFIRMED_PATH,
  CHECKOUT_V2_START_CHECKOUT_PATH,
  CHECKOUT_V2_START_CHECKOUT_USER_PARAMETER,
  DIRECT_SALES_CHECKOUT_V2_PATH,
  CHECKOUT_CARD_READER_FETCH_ALL_PATH,
  CHECKOUT_V2_EXPIRE_SESSION_PATH,
} from "./endpoints";
import { PaymentCollectionTarget } from "./payment-collection.types";
import { ICardReader } from "./card-reader.types";

export class CheckoutAPI extends BaseAPI {

  private static readonly POLL_ATTEMPTS = 45;

  private static readonly POLL_INTERVAL_MS = 2000;

  /**
   * This endpoint is called when a Setup/PaymentIntent associated with a basket has been confirmed
   * (either successfully or unsuccessfully), so that we can finish processing the basket and related charge.
   */
  public async postPaymentsBasketConfirmed(request: IBasketConfirmedRequest): Promise<IBasketConfirmedResponse> {
    const response = await this.axios.post(CHECKOUT_PAYMENTS_BASKET_CONFIRMED_PATH, request);
    return response.data;
  }

  async getBasket(basketId: string, signal?: AbortSignal) {

    const response = await this.axios.get<CheckoutBasket>(`/basket/${basketId}`, { signal });

    return response.data;
  }

  /** Polls the basket until it leaves IN_PROGRESS/DRAFT, the configured timeout
   * elapses, or the provided `signal` is aborted.
   *
   * @return null if the basket was still in progress after polling for the configured
   * duration (defaults to 90 seconds), or the not-in-progress basket, if one was found.
   * @throws Error (name: "AbortError") if aborted via `signal`.
   */
  async pollBasket(
    basketId: string,
    options?: {
      pollIntervalMs?: number,
      maxAttempts?: number,
      signal?: AbortSignal,
    },
  ): Promise<CheckoutBasket | null> {
    const { pollIntervalMs = CheckoutAPI.POLL_INTERVAL_MS, maxAttempts = CheckoutAPI.POLL_ATTEMPTS, signal } =
      options ?? {};

    for (let i = 0; i < maxAttempts; i++) {

      throwIfAborted(signal);

      const basket = await this.getBasket(basketId, signal);

      if (basket.status !== CheckoutStatus.IN_PROGRESS && basket.status !== CheckoutStatus.DRAFT) {
        return basket;
      }

      await sleep(pollIntervalMs, signal);
    }

    return null;

  }

  /**
   * Validates the recipe selection and calculates pricing with discounts for the subscriptions in the basket
   */
  public async getPriceEstimate(request: IGetPriceEstimateRequest): Promise<IGetPriceEstimateResponse> {
    const response = await this.axios.post(CHECKOUT_PRICE_ESTIMATE_PATH, request);
    return response.data as IGetPriceEstimateResponse;
  }

  /** Creates a checkout basket for the user and starts the checkout process for them.
   * Returns the basketId and stripeIntentSecret if successful, that will then
   * be used to process the payment.
   * May also return a result indicating that the customer has already checked out. */
  public async startCheckout(
    userId: string,
    basket: IStoredCheckoutBasket,
    paymentCollectionTarget: PaymentCollectionTarget = PaymentCollectionTarget.WEBSITE,
    persona?: TPersonaValues | null,
  ): Promise<CreateCheckoutBasketResponse> {
    try {
      const response = await this.axios.post(
        CHECKOUT_V2_START_CHECKOUT_PATH.replace(
          `:${CHECKOUT_V2_START_CHECKOUT_USER_PARAMETER}`,
          userId,
        ),
        {
          basket,
          paymentCollectionTarget,
          persona,
        },
      );

      return response.data as CreateCheckoutBasketResponse;
    } catch (error) {
      const response = isAxiosError(error) ? error.response : undefined;
      console.error("Error creating checkout basket", {
        status: response?.status,
        body: response?.data,
        error,
      });

      if (!response) {
        return {
          basketId: "",
          isExisting: false,
          validationError: {
            field: "network",
            message: "Network request failed",
            errorType: "network_error",
          },
        };
      }

      // If response exists, this is not a network failure.
      return {
        basketId: "",
        isExisting: false,
        validationError: {
          field: "basket",
          message: `Checkout request rejected with status ${response.status}`,
          errorType: "general",
        },
      };
    }
  }

  public async startDirectSalesCheckout(
    request: StartDirectSalesCheckoutRequest,
  ): Promise<StartDirectSalesCheckoutResult> {
    try {
      const response = await this.axios.post(
        DIRECT_SALES_CHECKOUT_V2_PATH,
        request,
      );
      return response.data as StartDirectSalesCheckoutResult;
    } catch (error) {
      console.error("Failed to process direct sales checkout", error);
      const responseData = (error as { response?: { data?: unknown } })?.response?.data;
      if (
        responseData &&
        typeof responseData === "object" &&
        "step" in responseData &&
        "message" in responseData
      ) {
        return { error: responseData as StartDirectSalesCheckoutResult["error"] };
      }
      return {
        error: {
          step: "UNKNOWN",
          message: "Network request failed",
        },
      };
    }
  }

  public async getCardReaders(): Promise<ICardReader[]> {
    const response = await this.axios.get(CHECKOUT_CARD_READER_FETCH_ALL_PATH);
    return response.data;
  }

  public async expireCheckoutSession(request: ExpireCheckoutSessionRequest): Promise<ExpireCheckoutSessionResponse> {
    const response = await this.axios.post(CHECKOUT_V2_EXPIRE_SESSION_PATH, request);
    return response.data;
  }

}

const createAbortError = (): Error => {
  const error = new Error("Polling aborted");
  error.name = "AbortError";
  return error;
};

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw createAbortError();
  }
};

const sleep = (n: number, signal?: AbortSignal): Promise<void> => new Promise((res, rej) => {
  if (signal?.aborted) {
    rej(createAbortError());
    return;
  }

  const timeoutId = setTimeout(() => {
    signal?.removeEventListener("abort", onAbort);
    res();
  }, n);

  const onAbort = () => {
    clearTimeout(timeoutId);
    rej(createAbortError());
  };
  signal?.addEventListener("abort", onAbort, { once: true });
});
